import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import cloneDeep from "lodash.clonedeep";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { hideLoader, showLoader } from '../../../store/common/slice';
import { changeMonthlyReportsSearchResults } from '../../../store/reports/slice';
import Table from "../../../common/table/TableFunctional";
import { constants } from '../../../common/constants/constants';
import BllinkUtils from '../../../common/utils/utils';
import { calcMonthsTextForSelectBox, getRowProps, shortenText } from '../../../common/shared/statelessMethods';
import PrintPdfIcons from '../../../common/export_documents/PrintPdfIcons';
import { selectCustomStyles } from '../../../common/shared/select.styled';
import Search from '../../../images/search.svg';
import Cheque from '../../../images/cheque.png';
import { StyledTableWrapper } from './MonthlyReportsTable.styled';
import { NotificationManager } from "react-notifications";
import { useAccessToken } from "../../../common/hooks/useAccessToken";
import { StyledPdfWrapper } from '../debtList/PdfWrapper.styled';
import MonthlyReportsPdfRow from './MonthlyReportsPdfRow';
import ErrorsHandler from "../../../common/errors/handleErrors";
import { getCreatedByPaymentPlanValue } from "../../../common/utils/parseFunctions";
import { isAdminSelector } from "../../../store/auth/selectors";

const MonthlyReportsTable = ({
     tableData,
     partnerID,
     tableTitle,
     titleToolTip,
     tableSubtitle,
     currentMonth,
     onMonthChange,
     year,
     exportTitle,
     building,
     showChequeDeposit,
     onChequeDepositClick,
     multipleBuildings
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();
    const pdfContainer = useRef();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const isAdmin = useSelector(isAdminSelector);

    const [title, setTitle] = useState('');
    const [listData, setListData] = useState([]);
    const [months, setMonths] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState({});
    const [filters, setFilters] = useState({});
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [hashavshevetExport, setHashavshevetExport] = useState(false);
    const [totalRecordsNum, setTotalRecordsNum] = useState(0);

    const titleRow = [{
        ApartmentNumber: t('reports.apartmentName'),
        ClientName: t('reports.tenantName'),
        buildingTitle: t('reports.buildingTitle'),
        Amount: t('reports.total_to_pay'),
        PaymentDate: t('reports.date_to_pay'),
        TenantType: t('reports.ownership_type'),
        paymentFor: t('reports.payment_for'),
        Comment: t('reports.payment_comment'),
        paymentID: t('payments.confirmation_id'),
        translatedMethod: t('payments.payment_method'),
        transferredAt: t('reports.transferredAt'),
        CreatedBy: t('reports.done_by')
    }];

    const fetchIdRef = useRef(0);
    const fetchData = useCallback(async ({ pageSize, pageIndex }) => {
        if (client == null) return;
        const fetchId = ++fetchIdRef.current;
        if (fetchId === fetchIdRef.current) {
            const startRow = pageSize * pageIndex;
            const endRow = startRow + pageSize;
            if (partnerID) {
                const data = await loadPartnerData(startRow, endRow);
                if (data) {
                    setPageCount(Math.ceil(data.totalRecords / pageSize));
                    setTotalRecordsNum(data.totalRecords);
                    setCurrentPage(pageIndex);
                    normalizeListData(data.paymentResults);
                    setListData(data.paymentResults);
                    dispatch(changeMonthlyReportsSearchResults({
                        Total: data.Total,
                        TotalInCreditCard: data.TotalInCreditCard,
                        TotalInCheque: data.TotalInCheque,
                        TotalFuture: data.TotalFuture.value,
                        TotalToWithdraw: data.TotalToWithdraw?.value,
                        currencyTotal: data.currency || constants.defaultCurrency,
                    }));
                }
            }
        }
    }, [client]);

    useEffect(() => {
        normalizeListData(tableData);
        building && setListData(tableData);
    }, [tableData]);

    useEffect(() => {
        getMonthsForDropdown(year);
    }, [currentMonth, year]);

    useEffect(async () => {
        if (client == null || building == null && partnerID == null) {
            // ignore this case
        } else {
            try {
                dispatch(showLoader());
                const fv = building == null ?
                    await client.partners.getFeatureValue(partnerID, 'accountingServiceIntegration') :
                    await client.tenants.buildings.getFeatureValue(building, 'accountingServiceIntegration') ;
                setHashavshevetExport(fv?.value === '1' || fv?.value === 'true');
            } catch (e) {
                NotificationManager.error(e.message);
            } finally {
                dispatch(hideLoader());
            }
        }
    }, [building, partnerID, client]);

    if (client == null) {
        return '';
    }

    async function parseData(type){
        return getParsedRows(listData, type);
    }

    function getParsedRows(data, type) {
        const keys = Object.keys(titleRow[0]);
        if (data && data.length > 0) {
            return data.map(row => {
                const item = cloneDeep(row);
                item.Amount = type === 'pdf' ? BllinkUtils.Strings.numberWithCommas(item.Amount) : item.Amount;
                item.PaymentDate = BllinkUtils.BllinkTime.parseDate(item.PaymentDate);
                item.TenantType = item.TenantType ? t(`reports.${item.TenantType}`) : '';
                const text = item.months ? calcMonthsTextForSelectBox(item.months) : shortenText(item.oneTime?.description);
                item.paymentFor = text?.shortComment || text;
                item.transferredAt = item.transferredAt ? new Date(item.transferredAt).toLocaleDateString('he-IL', {}) : '';
                // filter unnecessary fields from row
                const entries = Object.entries(item).filter(entry => keys.includes(entry[0]));
                return Object.fromEntries(entries);
            });
        }
    }

    async function loadPartnerData(start, end) {
        dispatch(showLoader());

        const response = await client.get(`/managers/cashflow-partner/${partnerID}?month=${currentMonth}&year=${year}`, {
            start,
            end
        });

        const data = response.value ?? response;
        data.paymentResults = data.paymentResults.map((res) => ({
            ...res,
            translatedMethod: res.Method ? t(`buildings.${res.Method}`) : ''
        }));

        try {
            // FIXME: that is a crazy idea to load dashboard data to get partner name!
            const data = await client.partners.dashboard.getStats(partnerID);
            setTitle(data.partnerStats.partner.partnerName);
        } catch (e) {
            ErrorsHandler.notifyClientError(e);
        }

        dispatch(hideLoader());
        return data;
    }

    async function loadAndParseAllPartnerData(type) {
        try {
            dispatch(showLoader());
            if (partnerID != null) {
                const data = await loadPartnerData(0, totalRecordsNum);
                if (data) {
                    return getParsedRows(data.paymentResults, type);
                }
            } else {
                throw new Error(t('reports.noPartnerIDError'));
            }
        } catch (e) {
            NotificationManager.error(e.message);
        } finally {
            dispatch(hideLoader());
        }
    }

    function parseAmount(amountString) {
        if (amountString) {
            return <span exceltext={amountString}>{BllinkUtils.Strings.numberWithCommas(amountString)}</span>
        }
    }

    function normalizeListData(data) {
        if (data?.length) {
            data.forEach(row => {
                row.CreatedBy = getCreatedByPaymentPlanValue(row.CreatedBy, t, isAdmin);
            })
        }
    }

    async function handleTablesLinksClicked(e, url) {
        e.preventDefault();

        dispatch(showLoader());
        const response = await client.get(url);
        dispatch(hideLoader());
        if (response) {
            let win = window.open(response, '_blank');
            win && win.focus();
        }
    }

    function renderCorrectReceipt(cartIdOrPaymentId, buildingID) {
        if (!cartIdOrPaymentId) return '';

        const isNumber = Number.isInteger(cartIdOrPaymentId);
        const linkText = t('reports.confirmation');
        let url;
        if (isNumber){
            url = `/managers/reports/confirmation-url/${buildingID}/${cartIdOrPaymentId}`;
            return <a href={url} onClick={e => handleTablesLinksClicked(e, url)}>{linkText}</a>
        } else {
            url = `/${constants.pages.orderStatusPage}/${cartIdOrPaymentId}`;
            return <Link to={url} target='_blank'>{linkText}</Link>;
        }
    }

    function getMonthsForDropdown(year) {
        let monthsOptions = [];
        const years = [parseInt(year) - 1, year, parseInt(year) + 1];
        for (const year of years) {
            for (const monthInt of Object.keys(constants.MonthsToText)) {
                monthsOptions.push({
                    value: `${monthInt}_${year}`,
                    label: `${t(`common.months.${monthInt}`)} ${year}`
                })
            }
        }

        const selectedMonthFromOptions = monthsOptions.find(option => option.value === `${currentMonth}_${year}`);
        setSelectedMonth(selectedMonthFromOptions);
        setMonths(monthsOptions);
    }

    const columns = [
        {
            Header: t('reports.apartmentName'),
            id: 'ApartmentNumber',
            accessor: 'ApartmentNumber',
        },
        {
            Header: t('reports.tenantName'),
            id: 'ClientName',
            accessor: 'ClientName',
        },
        {
            Header: t('reports.buildingTitle'),
            id: 'buildingTitle',
            accessor: 'buildingTitle',
            minWidth: 200
        },
        {
            Header: t('reports.total_to_pay'),
            id: 'Amount',
            accessor: 'Amount',
            className: 'amount',
            Cell: ({ row }) => {
                return <>{parseAmount(row.original.Amount)}</>
            }
        },
        {
            Header: t('reports.date_to_pay'),
            id: 'PaymentDate',
            accessor: 'PaymentDate',
            Cell: ({ row }) => {
                const dateParsed = BllinkUtils.BllinkTime.parseDate(row.original.PaymentDate);
                return <>{dateParsed}</>
            }
        },
        {
            Header: t('reports.ownership_type'),
            id: 'TenantType',
            Cell: ({ row }) => {
                const tenantTypeRaw = row.original.TenantType ? t(`reports.${row.original.TenantType}`) : '';
                return <>{tenantTypeRaw}</>
            }
        },
        {
            Header: t('reports.payment_for'),
            id: 'paymentFor',
            Cell: ({ row }) => {

                const text = row.original.months ? calcMonthsTextForSelectBox(row.original.months) : shortenText(row.original.oneTime?.description);
                return (
                    <OverlayTrigger
                        placement="top"

                        overlay={props => (
                            <Tooltip {...props}>
                                {text?.originalComment}
                            </Tooltip>
                        )}
                    >
                        <div exceltext={text?.shortComment || text}>
                            {text?.shortComment || text}
                        </div>
                    </OverlayTrigger>
                )
            }
        },
        {
            Header: t('reports.payment_comment'),
            id: 'Comment',
            accessor: 'Comment',
        },
        {
            Header: t('payments.confirmation_id'),
            id: 'paymentID',
            accessor: 'paymentID',
        },
        {
            Header: t('payments.payment_method'),
            id: 'Method',
            accessor: 'translatedMethod',
        },
        {
            Header: t('reports.confirmation'),
            id: 'cartID',
            accessor: 'cartID',
            Cell: ({ row }) => {
                const orig = row.original;
                const receiptLink = renderCorrectReceipt(orig.cartID, building || orig.buildingId);
                return <>{receiptLink}</>
            }
        },
        {
            Header: t('reports.receipt'),
            id: 'isOfflinePayment',
            accessor: 'isOfflinePayment',
            Cell: ({ row }) => {
                const {isOfflinePayment, invoiceDownloadLink, PaymentState, buildingID} = row.original;
                if (isOfflinePayment) {
                    return <a className="disabled" href='/'>{t('reports.receipt')}</a>
                }
                if (invoiceDownloadLink) {
                    const url = `/managers/reports/invoice-url/${building || buildingID}/${invoiceDownloadLink}`;
                    // disable link and add tooltip in case payment is in status in progress
                    const link = PaymentState === constants.APIContract.paymentStates.IN_PROGRESS ?
                        (<OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {t('reports.awaitingApproval')}
                                </Tooltip>
                            )}
                        >
                            <div>{t('reports.receipt')}</div>
                        </OverlayTrigger>) :
                        <a href={url} onClick={e => handleTablesLinksClicked(e, url)}>{t('reports.receipt')}</a>
                    return link
                }
                return '';
            }
        },
        // trying to add a hidden column and show it only on excel export. but didn't work
        {
            Header: t('reports.transferredAt'),
            id: 'transferredAt',
            accessor: 'transferredAt',
            Cell: ({ row }) => {
                // todo 'he-IL' make local
                const date = row.original.transferredAt ? new Date(row.original.transferredAt).toLocaleDateString('he-IL', {}) : '';
                // todo, this column is not visibile in excel export
                return <span exceltext={date}>{date}</span>
            }
        },
        {
            Header: t('reports.done_by'),
            id: 'createdBy',
            accessor: 'CreatedBy',
        },
    ];

    function onSearch(e) {
        setFilters({
            global: e.target.value
        });
    }

    async function handleHashavshevetExportClick() {
        let data = null;
        try {
            dispatch(showLoader());
            data = partnerID == null ?
                await client.managers.payments.getHashavshevetXmlBuildingData(building, year, currentMonth) :
                await client.managers.payments.getHashavshevetXmlPartnerData(partnerID, year, currentMonth) ;
        } catch (e) {
            NotificationManager.error(e.message);
        } finally {
            dispatch(hideLoader());
        }
        if (data != null) {
            const link = document.createElement('a');
            link.href = `data:application/zip;base64,${data.base64Data}`;
            link.download = data.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    const renderPdfRows = (row, i) => <MonthlyReportsPdfRow row={row} key={i} />

    return (
        <StyledTableWrapper ltr={ltr}>
            <div className={`cashflow_n ${partnerID ? 'noMargin' : ''}`}>
                {partnerID && (
                    <h1 className='page-title text-only centered'>
                        <span>{t('reports.partnerMonthlyReportTitle')}</span>
                        <span className='building-title'>{title}</span>
                    </h1>
                )}
                <div className='d-flex justify-content-between'>
                    <div className="d-flex justify-content-between">
                        <div className="srch">
                            <input type="search-alt-to-avoid-x" name="search" id="quick-search" onChange={onSearch} />
                            <a href="/" className="srch-btn non-clickable-cursor">
                                <img src={Search} alt={t('search.search')} />
                            </a>
                        </div>
                        <Select
                            styles={selectCustomStyles}
                            id="select-year"
                            onChange={onMonthChange}
                            options={months || []}
                            value={selectedMonth}
                        />
                    </div>

                    <div className='icons-wrapper'>
                        <PrintPdfIcons
                            pdfContainer={pdfContainer.current}
                            eventLabel={exportTitle}
                            titleString={exportTitle}
                            excelTitle={exportTitle}
                            reportName={t('reports.partnerMonthlyReportTitle')}
                            hashavshevet={hashavshevetExport}
                            onHashavshevet={handleHashavshevetExportClick}
                            buildingID={building}
                            rows={partnerID == null ? parseData : loadAndParseAllPartnerData}
                            partnerID={partnerID}
                            titleRow={titleRow}
                            notTable={true}
                            pdfIframe={true}
                            pdfSettings={{ chunkSize: 18, orientation: constants.pages.pdfOrientation.landscape }}
                            xlsSettings={{ columnTypes: [{index: 3, type: 'Number'}] }}
                        />
                        {showChequeDeposit && (
                            <OverlayTrigger
                                placement="top"
                                overlay={props => (
                                    <Tooltip {...props}>
                                        {t('reports.cheque_deposit_tooltip')}
                                    </Tooltip>
                                )}
                            >
                                <Link
                                    to={onChequeDepositClick}
                                    target="_blank"
                                    className="pdff"
                                >
                                    <img className="managerIcon" src={Cheque} alt="print-cheque-deposit"/>
                                </Link>
                            </OverlayTrigger>
                        )}
                    </div>
                </div>
            </div>

             <Table
                buildingId={building}
                tableTitle={tableTitle}
                tableSubtitle={tableSubtitle}
                titleToolTip={titleToolTip}
                columns={columns}
                data={listData}
                filters={filters}
                paginationOptions={{
                    fetchData: fetchData,
                    pageCount: pageCount,
                    currentPage: currentPage,
                    pageSize: partnerID ? 250 : 100
                }}
                hiddenColumns={multipleBuildings ? [] : ['buildingTitle']}
                getRowProps={getRowProps}
            />


            {listData ? (
                <div id="section-to-print" ref={pdfContainer}>
                    <StyledPdfWrapper ltr={ltr}>
                        <div className='pdf-title'>{t('reports.partnerMonthlyReportTitle')} {listData.length > 0 && listData[0].buildingTitle}</div>

                        <div className='pdf-rows-wrapper'>
                            <div className='pdf-row'>
                                <div className='pdf-highlited monthly-report'>{t('reports.apartmentName')}</div>
                                <div className='pdf-highlited monthly-report'>{t('reports.tenantName')}</div>
                                <div className='pdf-highlited monthly-report'>{t('reports.total_to_pay')}</div>
                                <div className='pdf-highlited monthly-report'>{t('reports.date_to_pay')}</div>
                                <div className='pdf-highlited monthly-report'>{t('reports.ownership_type')}</div>
                                <div className='pdf-highlited monthly-report'>{t('reports.payment_for')}</div>
                                <div className='pdf-highlited monthly-report'>{t('reports.payment_comment')}</div>
                                <div className='pdf-highlited monthly-report'>{t('payments.confirmation_id')}</div>
                                <div className='pdf-highlited monthly-report'>{t('payments.payment_method')}</div>
                                <div className='pdf-highlited monthly-report'>{t('reports.done_by')}</div>
                            </div>

                            {listData.map((row, i) => renderPdfRows(row, i))}
                        </div>
                    </StyledPdfWrapper>
                </div>
            ) : <div>{t('searchPayments.nothingFound')}</div>}

        </StyledTableWrapper>
    );
};

export default MonthlyReportsTable;
